/**
 * Parental-consent flow draft state (Steps 1–7).
 *
 * Persisted in sessionStorage so a child closing the tab + reopening
 * picks up where they left off. Cleared when the flow commits at
 * Step 7 (Wave 3.3) or when the user explicitly abandons.
 *
 * Critically: child PII beyond DOB is NOT stored anywhere except
 * here in the BROWSER until Step 7 commits server-side. This honours
 * the §22.5 + parent-consent-ux-spec data-minimisation rule:
 * "A parent who abandons mid-flow leaves no orphan row."
 */

const STORAGE_KEY = '4cm_consent_draft_v1';

export interface ConsentDraft {
  /** ISO YYYY-MM-DD. Required after Step 1. */
  childDob?: string;
  /** Parent's email. Required after Step 2. */
  parentEmail?: string;
  /** Optional first name for the email greeting. Step 1 may capture it
   *  later; for v0.1 we don't ask — the parent-invite email uses
   *  generic "your child" wording until the field is added. */
  childFirstName?: string;
  /** Random opaque token; used to tie the email link back to this
   *  flow (server-side validation lands in Wave 3.3 commit fn). */
  inviteToken?: string;
  /** Step 4 (parent-side) — flipped true when parent has scrolled
   *  through the privacy notice. */
  privacyNoticeRead?: boolean;
  /** ISO timestamp of the last draft write — used to invalidate
   *  stale drafts (>7 days) on read. */
  updatedAt?: string;
}

const MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000; // 7 days

export function getConsentDraft(): ConsentDraft | null {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as ConsentDraft;
    // Stale-draft guard.
    if (parsed.updatedAt) {
      const age = Date.now() - new Date(parsed.updatedAt).getTime();
      if (Number.isFinite(age) && age > MAX_AGE_MS) {
        sessionStorage.removeItem(STORAGE_KEY);
        return null;
      }
    }
    return parsed;
  } catch {
    return null;
  }
}

export function setConsentDraft(patch: Partial<ConsentDraft>): ConsentDraft {
  const current = getConsentDraft() ?? {};
  const next: ConsentDraft = {
    ...current,
    ...patch,
    updatedAt: new Date().toISOString(),
  };
  if (typeof sessionStorage !== 'undefined') {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    } catch {
      /* quota exceeded — surface UI banner if this matters */
    }
  }
  return next;
}

export function clearConsentDraft(): void {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    /* ignore */
  }
}

/** Generate a random invite token. UUIDv4-shaped, browser-side only. */
export function newInviteToken(): string {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  // Fallback: weak but acceptable for client-side opaque tokens.
  return Array.from({ length: 32 }, () => Math.floor(Math.random() * 16).toString(16)).join('');
}
